/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-native/no-color-literals */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  TouchableOpacity,
  ScrollView,
  Modal,
  KeyboardAvoidingView,
  Keyboard,
} from 'react-native';
import { Portal } from '@gorhom/portal';
import {
  SafeAreaView,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';
import Colors from '@global/colors';
import { isWeb, isMobileWeb, isAndroid } from '@global/screens';
import useAndroidKeyboardHeight from '@/hooks/useAndroidKeyboardHeight';
import { MsB2b } from '@api/ms-b2b';
import CommentList from './CommentList';
import CommentInput from './CommentInput';
import ManageCommentSheet from './ManageCommentSheet';

const LIMIT = 20;

const CommentDrawer = ({
  visible,
  onClose,
  entityType,
  entityId,
  entityRef,
}) => {
  const insets = useSafeAreaInsets();
  const keyboardHeight = useAndroidKeyboardHeight();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const scrollViewRef = useRef(null);
  const listContainerRef = useRef(null);

  // Lets an edited MessageItem check the visible list area before opening its mention
  // dropdown, so the dropdown opens toward the side that has room instead of being clipped.
  const getListRect = (callback) => {
    if (!listContainerRef.current?.measureInWindow) {
      callback(null);
      return;
    }
    listContainerRef.current.measureInWindow((x, y, width, height) =>
      callback({ top: y, bottom: y + height }),
    );
  };
  const pendingScrollToBottom = useRef(false);
  const [manageMenu, setManageMenu] = useState(null);
  // Only one comment is editable at a time; while one is open, the others hide their
  // manage menu (MessageItem) so a second edit box / mention dropdown can't open.
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (pendingScrollToBottom.current) {
      pendingScrollToBottom.current = false;
      requestAnimationFrame(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      });
    }
  }, [comments]);

  useEffect(() => {
    const subscription = Keyboard.addListener('keyboardDidShow', () => {
      requestAnimationFrame(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      });
    });
    return () => subscription.remove();
  }, []);

  const fetchComments = useCallback(
    async (nextSkip, prepend, fetchLimit) => {
      setLoading(true);
      try {
        const filter = { entity_type: entityType, entity_id: entityId };
        let resolvedSkip = nextSkip;
        let knownTotal = total;
        if (resolvedSkip === undefined) {
          const countResponse = await MsB2b.getCommentsCount({
            params: { filter },
          });
          knownTotal = countResponse?.data?.data || 0;
          resolvedSkip = Math.max(0, knownTotal - LIMIT);
        }
        const response = await MsB2b.getComments({
          params: {
            filter,
            sort: 'created_at',
            skip: resolvedSkip,
            limit: fetchLimit || LIMIT,
          },
        });
        const data = response?.data?.data || [];
        setComments((prev) => {
          if (!prepend) {
            return data;
          }
          const existingIds = new Set(prev.map((c) => c._id));
          return [...data.filter((c) => !existingIds.has(c._id)), ...prev];
        });
        setSkip(resolvedSkip);
        setTotal(knownTotal);
      } catch (e) {
        setComments([]);
      }
      setLoading(false);
    },
    [entityType, entityId, total],
  );

  useEffect(() => {
    if (visible && entityId) {
      setEditingId(null);
      // Only the initial open should jump to the bottom — "load earlier" prepends older
      // comments above the current view and must not yank the scroll position back down.
      pendingScrollToBottom.current = true;
      fetchComments(undefined, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, entityId]);

  const onLoadMore = () => {
    const newSkip = Math.max(0, skip - LIMIT);
    fetchComments(newSkip, true, skip - newSkip);
  };

  const onSubmit = async (content, mentions) => {
    const response = await MsB2b.createComment({
      entity_type: entityType,
      entity_id: entityId,
      entity_ref: entityRef,
      content,
      mentions,
    });
    if (response?.data?.data) {
      pendingScrollToBottom.current = true;
      setComments((prev) => [...prev, response.data.data]);
    }
  };

  const onEdit = async (id, content, mentions) => {
    const response = await MsB2b.updateComment(id, {
      content,
      mentions,
    });
    if (response?.data?.data) {
      setComments((prev) =>
        prev.map((c) => (c._id === id ? response.data.data : c)),
      );
    }
  };

  const onDelete = async (id) => {
    await MsB2b.deleteComment(id);
    setComments((prev) => prev.filter((c) => c._id !== id));
    setEditingId((current) => (current === id ? null : current));
  };

  const hasMore = skip > 0;

  const Body = (
    <>
      {/* collapsable={false}: a layout-only View is flattened away on Android, and
          measureInWindow on it returns undefined (see getListRect). */}
      <View ref={listContainerRef} collapsable={false} style={{ flex: 1 }}>
        <ScrollView
          ref={scrollViewRef}
          style={{ flex: 1 }}
          contentContainerStyle={{ flexGrow: 1 }}>
          <CommentList
            loading={loading}
            comments={comments}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            onEdit={onEdit}
            onDelete={onDelete}
            onOpenManage={setManageMenu}
            getListRect={getListRect}
            editingId={editingId}
            onEditingChange={setEditingId}
          />
        </ScrollView>
      </View>
      {/* The modal window spans the nav bar, so the input row clears it itself.
          While the keyboard is up it already covers that area, so drop to 0.
          SafeAreaView (not useSafeAreaInsets) measures the modal's own window: on
          Android <= 15 the app opts out of edge-to-edge, so the root insets are 0.
          iOS keeps useSafeAreaInsets: inside a non-transparent iOS Modal, SafeAreaView
          reports 0 insets. Web and mobile web take this branch too, where insets are 0. */}
      {isAndroid ? (
        <SafeAreaView edges={keyboardHeight ? [] : ['bottom']}>
          <CommentInput onSubmit={onSubmit} />
        </SafeAreaView>
      ) : (
        <View style={{ paddingBottom: insets.bottom }}>
          <CommentInput onSubmit={onSubmit} />
        </View>
      )}
      <ManageCommentSheet
        visible={!!manageMenu}
        anchorRect={manageMenu?.anchorRect}
        onClose={() => setManageMenu(null)}
        onEdit={() => {
          manageMenu?.onEdit();
          setManageMenu(null);
          setTimeout(() => {
            scrollViewRef.current?.scrollToEnd({ animated: true });
          }, 250);
        }}
        onDelete={() => {
          manageMenu?.onDelete();
          setManageMenu(null);
        }}
      />
    </>
  );

  if (!visible) {
    return null;
  }

  const HeaderWrapper = isAndroid ? SafeAreaView : View;

  if (isWeb) {
    return (
      <Portal>
        <TouchableOpacity
          testID="comment-drawer-overlay"
          activeOpacity={1}
          onPress={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#00000099',
            zIndex: 20,
          }}
        />
        <View
          testID="comment-drawer"
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: 480,
            maxWidth: '100%',
            backgroundColor: Colors.white,
            zIndex: 21,
            flexDirection: 'column',
            boxShadow: '-2px 0 10px rgba(0,0,0,0.15)',
          }}>
          <View
            style={{
              paddingVertical: 16,
              paddingHorizontal: 20,
              borderBottomWidth: 1,
              borderBottomColor: Colors.grey0,
            }}>
            <SociollaText type="bold" style={{ fontSize: 18 }}>
              Comments
            </SociollaText>
          </View>
          {Body}
        </View>
      </Portal>
    );
  }

  if (isMobileWeb) {
    return (
      <Portal>
        <View
          testID="comment-drawer"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: Colors.white,
            zIndex: 21,
            flexDirection: 'column',
          }}>
          <KeyboardAvoidingView behavior="height" style={{ flex: 1 }}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: Colors.blue1,
                paddingTop: insets.top + 12,
                paddingBottom: 12,
                paddingHorizontal: 16,
              }}>
              <TouchableOpacity testID="comment-drawer-back" onPress={onClose}>
                <Image
                  source={require('@assets/img/icons/arrow-left-white.svg')}
                  style={{ width: 18, height: 18, marginRight: 14 }}
                  width={18}
                  height={18}
                  svg={true}
                />
              </TouchableOpacity>
              <SociollaText
                type="bold"
                style={{ fontSize: 16, color: Colors.white }}>
                Comments
              </SociollaText>
            </View>
            {Body}
          </KeyboardAvoidingView>
        </View>
      </Portal>
    );
  }

  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={onClose}
      statusBarTranslucent
      navigationBarTranslucent>
      <KeyboardAvoidingView
        behavior={isAndroid ? undefined : 'height'}
        style={{
          flex: 1,
          ...(isAndroid ? { marginBottom: keyboardHeight } : {}),
        }}>
        {/* The modal is statusBarTranslucent, so the header clears the status bar via
            SafeAreaView — same as components/modal/Modal.js — because the root insets
            are 0 on Android <= 15, where the app still opts out of edge-to-edge.
            iOS pads by insets.top instead: SafeAreaView reports 0 inside this modal. */}
        <HeaderWrapper
          {...(isAndroid ? { edges: ['top'] } : {})}
          style={{
            backgroundColor: Colors.blue1,
            ...(isAndroid ? {} : { paddingTop: insets.top }),
          }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              paddingVertical: 12,
              paddingHorizontal: 16,
            }}>
            <TouchableOpacity testID="comment-drawer-back" onPress={onClose}>
              <Image
                source={require('@assets/img/icons/arrow-left-white.svg')}
                style={{ width: 18, height: 18, marginRight: 14 }}
                width={18}
                height={18}
                svg={true}
              />
            </TouchableOpacity>
            <SociollaText
              type="bold"
              style={{ fontSize: 16, color: Colors.white }}>
              Comments
            </SociollaText>
          </View>
        </HeaderWrapper>
        {Body}
      </KeyboardAvoidingView>
    </Modal>
  );
};

export default CommentDrawer;
